import { useEffect, useMemo, useState } from 'react';
import { Outlet, Navigate, useParams, useNavigate, useLocation } from 'react-router';
import {
  Box,
  Button,
  Typography,
  Tabs,
  Tab,
  Avatar,
  AvatarGroup,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  TextField,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useTranslation } from 'react-i18next';
import { serverTimestamp, Timestamp } from 'firebase/firestore';
import type { Client, ProjectTeam, ProgramType, User } from '@altyor-kronos/shared-types';
import { flattenTeam, PROGRAM_TYPES, emptyTeam } from '@altyor-kronos/shared-types';
import { apiGet, ApiError } from '../core/data/restClient';
import { useAuth } from '../core/auth/useAuth';
import { visibleTabs, type ProjectTab, normalizeProjectCode } from '../features/projects/validators';
import { useProjectsHubData } from '../features/projects/useProjectsHubData';
import { useProjectDoc } from '../features/projects/useProjectDoc';
import { useUsersList } from '../features/projects/useUsersList';
import { advanceProgramHistory, updateProject } from '../core/data/projectMutations';
import { colors } from '../core/theme/theme';
import type { ProjectDetailEditState } from '../features/projects/ProjectOverview';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ProjectDraft {
  name: string;
  code: string;
  clientId: string;
  program: ProgramType;
  sharepointLink: string;
  teamsLink: string;
  customerWebsite: string;
  team: ProjectTeam;
}

// ─── Style constants ─────────────────────────────────────────────────────────

/** Shared hero text style — white with a subtle drop shadow for legibility. */
const heroTextSx = {
  color: colors.white,
  textShadow: '0 1px 2px rgba(0,0,0,0.4)',
} as const;

/** Glassmorphism style for action buttons on the hero photo. */
const glassButtonSx = {
  backgroundColor: 'rgba(255,255,255,0.9)',
  '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
} as const;

/** Input styling for fields rendered directly on the dark hero background. */
const heroInputSx = {
  '& .MuiInputBase-input': { color: colors.white },
  '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.5)', opacity: 1 },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.4)' },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.8)' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.white },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
  '& .MuiInputLabel-root.Mui-focused': { color: colors.white },
  '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.7)' },
} as const;

/**
 * Project detail layout shell.
 *
 * Fetches the project doc (real-time), resolves the client + PM users, and
 * renders a header + role-gated horizontal tab bar. Tab content is supplied
 * by the nested <Outlet />.
 *
 * Edit mode: authorized users (admin or per-project PM) can click any
 * editable field directly on the page. A sticky save / cancel bar appears
 * as soon as any field is changed. No separate wizard dialog is used for
 * editing — the ProjectWizard is reserved for project creation only.
 */
export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Subscribe to the project doc live
  const { project, loading, error: projectDocError } = useProjectDoc(id);
  const [client, setClient] = useState<Client | null>(null);
  const [managers, setManagers] = useState<User[]>([]);
  const [team, setTeam] = useState<User[]>([]);
  // Set by the client/team-resolution effect below on failure; the live
  // project-doc subscription reports its own errors via `projectDocError`.
  const [resolutionError, setResolutionError] = useState<Error | null>(null);
  const error = projectDocError ?? resolutionError;

  // Resolve client + team when the project loads/changes
  useEffect(() => {
    if (!project) return;
    let cancelled = false;

    // Resolve the client + team/manager user docs through the same-origin /api
    // routes (per-doc reads are open to any authenticated user — mirrors a
    // per-doc read, not a scoped collection list).
    (async () => {
      if (project.clientId) {
        try {
          setClient(await apiGet<Client>(`/clients/${project.clientId}`));
        } catch (e) {
          if (e instanceof ApiError && e.status === 404) setClient(null);
          else throw e;
        }
      } else {
        setClient(null);
      }
      if (cancelled) return;

      const managerUids = project.team?.managers ?? [];
      const teamUids = project.team ? flattenTeam(project.team) : [];
      const uids = Array.from(new Set([...managerUids, ...teamUids]));
      if (uids.length === 0) {
        setManagers([]);
        setTeam([]);
        return;
      }
      const byUid = new Map<string, User>();
      const docs = await Promise.all(
        uids.map((u) =>
          apiGet<User>(`/users/${u}`).catch((e) => {
            if (e instanceof ApiError && e.status === 404) return null;
            throw e;
          }),
        ),
      );
      uids.forEach((u, i) => { const d = docs[i]; if (d) byUid.set(u, d); });
      if (cancelled) return;
      setManagers(managerUids.map((u) => byUid.get(u)).filter(Boolean) as User[]);
      setTeam(teamUids.map((u) => byUid.get(u)).filter(Boolean) as User[]);
    })().catch((err) => {
      if (!cancelled) setResolutionError(err);
    });

    return () => {
      cancelled = true;
    };
  }, [project]);

  // ── Edit draft state ─────────────────────────────────────────────────────
  // editedFields tracks only the fields the user has explicitly changed.
  // Display always uses: editedFields[field] ?? project[field] (draft-first).
  const [isEditing, setIsEditing] = useState(false);
  const [editedFields, setEditedFields] = useState<Partial<ProjectDraft>>({});
  const [activeEditField, setActiveEditField] = useState<'name' | 'code' | 'program' | null>(null);
  const [saving, setSaving] = useState(false);

  const isDirty = Object.keys(editedFields).length > 0;

  // Reset draft and exit edit mode when navigating to a different project
  useEffect(() => {
    setIsEditing(false);
    setEditedFields({});
    setActiveEditField(null);
  }, [id]);

  const patchDraft = (update: Partial<ProjectDraft>) => {
    setEditedFields((prev) => ({ ...prev, ...update }));
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditedFields({});
    setActiveEditField(null);
  };

  // ── Clients + users data for inline pickers ──────────────────────────────
  const { projects: allProjects, clients: clientsMap } = useProjectsHubData();
  // Load users for all authenticated users — Firestore rules allow public read
  // within the organisation. Gating by role here would complicate the hook
  // ordering since canEdit depends on project data (available after loading).
  const { users: allUsers } = useUsersList(true);

  const clientsArr = useMemo(
    () => Array.from(clientsMap.entries()).map(([cid, data]) => ({ id: cid, data })),
    [clientsMap],
  );
  const existingCodes = useMemo(
    () => allProjects.map((p) => ({ id: p.id, code: p.data.code })),
    [allProjects],
  );
  void existingCodes; // available for future code-uniqueness validation

  // ── Save handler ─────────────────────────────────────────────────────────
  const handleSaveEdit = async () => {
    if (!id || !project || saving) return;
    setSaving(true);
    try {
      const mergedTeam = editedFields.team ?? project.team ?? emptyTeam();
      const nextProgram = editedFields.program ?? project.currentProgram;
      // A program change has to close the running phase and open a new one, or
      // the Overview frieze keeps showing the previous phase as ongoing. Null
      // when the program did not actually move — nothing to rewrite then.
      const nextHistory = advanceProgramHistory(project.programHistory, nextProgram, Timestamp.now());
      await updateProject(id, {
        name: (editedFields.name ?? project.name).trim(),
        code: normalizeProjectCode(editedFields.code ?? project.code),
        clientId: editedFields.clientId ?? project.clientId,
        currentProgram: nextProgram,
        ...(nextHistory ? { programHistory: nextHistory } : {}),
        team: mergedTeam,
        allMemberUids: flattenTeam(mergedTeam),
        sharepointLink: (editedFields.sharepointLink ?? project.sharepointLink ?? '') || null,
        teamsLink: (editedFields.teamsLink ?? project.teamsLink ?? '') || null,
        customerWebsite: (editedFields.customerWebsite ?? project.customerWebsite ?? '') || null,
        updatedAt: serverTimestamp(),
      });
      setIsEditing(false);
      setEditedFields({});
      setActiveEditField(null);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Project save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error.message}</Alert>;
  }

  if (!project || !user) {
    return <Alert severity="warning">{t('projects.detail.notFound')}</Alert>;
  }

  // ── Derived permissions ──────────────────────────────────────────────────
  const tabs: ProjectTab[] = visibleTabs(user, { ...project, id: id! });
  const isProjectManager = project.team?.managers?.includes(user.uid) ?? false;
  const isAdmin = user.roles.includes('admin');
  const canEdit = isProjectManager || isAdmin;

  const currentTab = tabs.findIndex((tab) =>
    tab.path ? location.pathname.endsWith(`/${tab.path}`) : location.pathname === `/projects/${id}`,
  );

  // Un seul garde pour tous les onglets, présents et futurs : `visibleTabs()` porte
  // déjà la politique, mais elle ne servait qu'à DESSINER la barre — les routes
  // enfants (App.tsx) sont nues, donc une URL tapée rendait un onglet masqué, et le
  // `Math.max(0, …)` d'avant surlignait « Aperçu » pendant ce temps-là. Emballer
  // chaque route séparément aurait laissé passer la prochaine.
  //
  // Ne remplace pas une garde serveur : Finance et Avant-Projet restent lisibles via
  // l'API (`designations`/`hourlyRates` ouverts, et l'AP vit sur le doc projet).
  if (currentTab === -1) return <Navigate to={`/projects/${id}`} replace />;

  // ── Draft-first computed values ──────────────────────────────────────────
  const draftName = editedFields.name ?? project.name;
  const draftCode = editedFields.code ?? project.code;
  const draftClientId = editedFields.clientId ?? project.clientId;
  const draftProgram = editedFields.program ?? project.currentProgram;
  const draftSharepointLink = editedFields.sharepointLink ?? project.sharepointLink ?? '';
  const draftTeamsLink = editedFields.teamsLink ?? project.teamsLink ?? '';
  const draftCustomerWebsite = editedFields.customerWebsite ?? project.customerWebsite ?? '';
  const draftTeam = editedFields.team ?? project.team ?? emptyTeam();

  // Display client name from the clients map (handles clientId changes in draft)
  const displayClientName =
    clientsArr.find((c) => c.id === draftClientId)?.data.name ?? client?.name ?? '';

  // ── Hero background ───────────────────────────────────────────────────────
  const HERO_STATIC = [
    '/images/heroes/hero-1.webp',
    '/images/heroes/hero-2.webp',
    '/images/heroes/hero-3.webp',
    '/images/heroes/hero-4.webp',
    '/images/heroes/hero-5.webp',
    '/images/heroes/hero-6.webp',
    '/images/heroes/hero-7.webp',
    '/images/heroes/hero-8.webp',
  ];
  const staticHero =
    HERO_STATIC[
      (id ?? '').split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0) % HERO_STATIC.length
    ];
  const heroPhoto = project.photos?.[0] ?? staticHero;

  // ── Edit state passed to Overview via outlet context ─────────────────────
  // Only passed when the user has explicitly clicked "Edit" — so everyone
  // sees the same read-only view on landing.
  const editState: ProjectDetailEditState | null = canEdit && isEditing
    ? {
        draft: {
          sharepointLink: draftSharepointLink,
          teamsLink: draftTeamsLink,
          customerWebsite: draftCustomerWebsite,
          team: draftTeam,
        },
        patch: patchDraft,
        allUsers,
      }
    : null;

  return (
    <Box sx={{ pb: isDirty ? 8 : 0 }}>
      {/* Header — hero image with dark overlay, full-bleed ──── */}
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: 180, md: 240 },
          mb: 2,
          overflow: 'hidden',
          mx: { xs: -2, sm: -3, md: -5 },
          mt: { xs: -2, sm: -3, md: -5 },
          backgroundImage: `url(${heroPhoto})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'stretch',
        }}
        data-testid="project-detail-header"
      >
        {/* Dark gradient overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.65) 100%)',
          }}
        />
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{
            position: 'relative',
            zIndex: 1,
            px: { xs: 2, sm: 3, md: 5 },
            py: 2,
            width: '100%',
            alignItems: { xs: 'flex-start', sm: 'flex-end' },
          }}
        >
          {/* Left — name, code, client */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Code */}
            {isEditing && activeEditField === 'code' ? (
              <TextField
                value={draftCode}
                onChange={(e) => patchDraft({ code: e.target.value.toUpperCase() })}
                onBlur={() => setActiveEditField(null)}
                onKeyDown={(e) => { if (e.key === 'Enter') setActiveEditField(null); }}
                autoFocus
                size="small"
                variant="outlined"
                sx={{ mb: 0.5, width: 140, ...heroInputSx }}
                slotProps={{ htmlInput: { style: { letterSpacing: 0.5, fontSize: '0.75rem' } } }}
              />
            ) : (
              <Typography
                variant="caption"
                sx={{
                  letterSpacing: 0.5,
                  display: 'block',
                  ...heroTextSx,
                  ...(isEditing && { cursor: 'text', '&:hover': { opacity: 0.75 } }),
                }}
                onClick={isEditing ? () => setActiveEditField('code') : undefined}
                title={isEditing ? t('projects.detail.clickToEdit', 'Click to edit') : undefined}
              >
                {draftCode}
              </Typography>
            )}

            {/* Name */}
            {isEditing && activeEditField === 'name' ? (
              <TextField
                value={draftName}
                onChange={(e) => patchDraft({ name: e.target.value })}
                onBlur={() => setActiveEditField(null)}
                onKeyDown={(e) => { if (e.key === 'Enter') setActiveEditField(null); }}
                autoFocus
                variant="outlined"
                size="small"
                fullWidth
                sx={{
                  ...heroInputSx,
                  '& .MuiInputBase-input': {
                    ...heroInputSx['& .MuiInputBase-input'],
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    lineHeight: 1.1,
                  },
                }}
              />
            ) : (
              <Typography
                variant="h2"
                sx={{
                  lineHeight: 1.1,
                  ...heroTextSx,
                  ...(isEditing && { cursor: 'text', '&:hover': { opacity: 0.85 } }),
                }}
                onClick={isEditing ? () => setActiveEditField('name') : undefined}
                title={isEditing ? t('projects.detail.clickToEdit', 'Click to edit') : undefined}
              >
                {draftName}
              </Typography>
            )}

            {/* Client */}
            {displayClientName ? (
              <Typography
                component="button"
                type="button"
                variant="body1"
                onClick={() => navigate(`/customers/${draftClientId}`)}
                data-testid="project-client-link"
                sx={{
                  mt: 0.5,
                  display: 'inline-block',
                  ...heroTextSx,
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  textAlign: 'left',
                  '&:hover': { opacity: 0.85, textDecoration: 'underline' },
                }}
              >
                {displayClientName}
              </Typography>
            ) : null}
          </Box>

          {/* Right — program, avatars, actions */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ alignItems: { xs: 'stretch', sm: 'center' }, flexWrap: 'wrap', flexShrink: 0 }}
          >
            {/* Meta group — program, avatars */}
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1, rowGap: 1 }}
              useFlexGap
            >
            {/* Program — click to change when canEdit */}
            {isEditing && activeEditField === 'program' ? (
              <Box
                sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}
                onBlur={(e) => {
                  // Close selector when focus leaves the program chips area
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    setActiveEditField(null);
                  }
                }}
              >
                {PROGRAM_TYPES.map((p) => (
                  <Chip
                    key={p}
                    label={p}
                    size="small"
                    tabIndex={0}
                    onClick={() => { patchDraft({ program: p }); setActiveEditField(null); }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        patchDraft({ program: p });
                        setActiveEditField(null);
                      }
                    }}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: '6px',
                      backgroundColor:
                        p === draftProgram ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.5)',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.85)' },
                      fontWeight: p === draftProgram ? 700 : 400,
                    }}
                  />
                ))}
              </Box>
            ) : (
              <Chip
                label={draftProgram}
                size="small"
                variant="outlined"
                onClick={isEditing ? () => setActiveEditField('program') : undefined}
                data-testid="project-program-chip"
                sx={{
                  borderRadius: '6px',
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  borderColor: 'rgba(255,255,255,0.9)',
                  cursor: isEditing ? 'pointer' : 'default',
                  '&:hover': isEditing ? { backgroundColor: 'rgba(255,255,255,1)' } : {},
                }}
              />
            )}

            {managers.length > 0 && (
              <AvatarGroup
                max={4}
                sx={{ '& .MuiAvatar-root': { borderRadius: 0, width: 32, height: 32 } }}
                data-testid="project-managers-avatars"
              >
                {managers.map((m) => (
                  <Avatar key={m.uid} src={m.photoURL ?? undefined} alt={m.displayName}>
                    {m.displayName?.charAt(0) ?? '?'}
                  </Avatar>
                ))}
              </AvatarGroup>
            )}
            </Stack>

            {/* Actions group — full-width buttons on mobile */}
            {canEdit && (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{ alignItems: 'stretch', flexShrink: 0 }}
              >
                {canEdit && !isEditing && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditOutlinedIcon />}
                    onClick={() => setIsEditing(true)}
                    sx={glassButtonSx}
                    data-testid="project-edit-button"
                  >
                    {t('common.edit')}
                  </Button>
                )}
                {canEdit && isEditing && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={cancelEdit}
                    sx={glassButtonSx}
                    data-testid="project-cancel-edit-button"
                  >
                    {t('common.cancel')}
                  </Button>
                )}

              </Stack>
            )}
          </Stack>
        </Stack>
      </Box>

      {/* Tab bar */}
      <Tabs
        value={currentTab}
        onChange={(_, newValue) => {
          const tab = tabs[newValue];
          navigate(`/projects/${id}${tab.path ? `/${tab.path}` : ''}`);
        }}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 3, borderBottom: `1px solid ${colors.lightGray}` }}
        data-testid="project-tabbar"
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.key}
            label={t(`projects.${tab.key}`)}
            data-testid={`project-tab-${tab.key}`}
          />
        ))}
      </Tabs>

      {/* Tab content */}
      <Outlet
        context={{
          project: { ...project, id: id! },
          client,
          managers,
          team,
          editState,
        }}
      />

      {/* Sticky save / cancel bar — visible only when there are unsaved changes */}
      {isDirty && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1200,
            bgcolor: colors.bodyText,
            px: 3,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 -2px 12px rgba(0,0,0,0.25)',
          }}
          data-testid="project-edit-save-bar"
        >
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            {t('projects.detail.unsavedChanges', 'Unsaved changes')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={cancelEdit}
              disabled={saving}
              sx={{
                color: colors.white,
                borderColor: 'rgba(255,255,255,0.4)',
                '&:hover': { borderColor: colors.white, backgroundColor: 'rgba(255,255,255,0.08)' },
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => void handleSaveEdit()}
              disabled={saving}
              sx={{
                bgcolor: colors.yellow,
                color: colors.bodyText,
                '&:hover': { bgcolor: colors.yellow, opacity: 0.9 },
                '&:disabled': { bgcolor: colors.yellow, opacity: 0.5 },
              }}
            >
              {saving ? (
                <CircularProgress size={16} sx={{ color: colors.bodyText }} />
              ) : (
                t('common.save')
              )}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
